import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Lock, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix 4: owner-managed per-user module access matrix.
export const ManagePermissionsDialog = ({ targetUser, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [modules, setModules] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!targetUser) return;
    setData(null);
    axios.get(`${API}/users/${targetUser.id}/module-permissions`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { setData(r.data); setModules(r.data.modules); })
      .catch(e => { toast.error(e.response?.data?.detail || 'Failed to load permissions'); onOpenChange(false); });
  }, [targetUser]);

  const groups = useMemo(() => (data?.catalog || []).reduce((acc, m) => {
    (acc[m.group] = acc[m.group] || []).push(m); return acc;
  }, {}), [data]);

  const setAll = (v) => setModules(Object.fromEntries((data?.catalog || []).map(m => [m.key, v])));

  const [saved, setSaved] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(`${API}/users/${targetUser.id}/module-permissions`, { modules }, { headers: { Authorization: `Bearer ${token}` } });
      setModules(r.data.modules || modules);
      setSaved(true);
      setTimeout(() => { setSaved(false); onOpenChange(false); }, 1200);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save permissions');
    } finally { setSaving(false); }
  };

  const locked = data?.locked;
  return (
    <Dialog open={!!targetUser} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-white/80 backdrop-blur-xl border-white/60" data-testid="manage-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} />Manage Permissions — {targetUser?.name}</DialogTitle>
          <DialogDescription>
            {locked ? 'Owner accounts always have access to every module.' : 'Turn modules on or off for this user. Changes apply on their next page load.'}
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-stone-400" /></div>
        ) : (
          <div className="space-y-5">
            {!locked && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAll(true)} data-testid="perm-enable-all">Enable all</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAll(false)} data-testid="perm-disable-all">Disable all</Button>
              </div>
            )}
            {Object.entries(groups).map(([group, items]) => (
              <div key={group}>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-2">{group}</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {items.map(m => (
                    <div key={m.key} className="flex items-center justify-between rounded-xl border border-white/70 bg-white/50 px-3 py-2 text-sm">
                      <span className="text-stone-800">{m.label}</span>
                      {locked ? <Lock size={14} className="text-stone-400" /> : (
                        <Switch checked={!!modules[m.key]} onCheckedChange={(v) => setModules(p => ({ ...p, [m.key]: v }))} data-testid={`perm-toggle-${m.key}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {saved && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/90 px-3 py-2 text-sm font-medium text-emerald-800" data-testid="perm-save-success">
            Permissions saved — {targetUser?.name} will see the change on their next page load.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="perm-cancel-btn">Close</Button>
          {!locked && data && (
            <Button onClick={save} disabled={saving || saved} className="rounded-full" data-testid="perm-save-btn">
              {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Save permissions
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
